import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let openCount = 0;

// Accessible modal container shared by every modal in the app:
// portal to <body>, Esc / backdrop to close, body scroll lock, focus moved in and restored,
// bottom sheet on phones and centred dialog from `sm` up.
const ModalShell = ({ isOpen, onClose, children, panelClassName = 'sm:max-w-4xl', labelledBy, closeOnBackdrop = true }) => {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const fallbackId = useId();
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const previouslyFocused = document.activeElement;
    openCount += 1;
    document.body.style.overflow = 'hidden';

    const focusTimer = setTimeout(() => {
      const panel = panelRef.current;
      if (!panel || panel.contains(document.activeElement)) return;
      const preferred = panel.querySelector('[data-autofocus]') || panel.querySelector('input:not([type="hidden"]):not([disabled]), textarea, select');
      (preferred || panel).focus({ preventScroll: true });
    }, 30);

    const handleKey = (e) => {
      if (e.key === 'Escape' && onCloseRef.current) {
        e.stopPropagation();
        onCloseRef.current();
      }
      if (e.key === 'Tab' && panelRef.current) {
        const items = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter(el => el.offsetParent !== null);
        if (!items.length) return;
        const [first, last] = [items[0], items[items.length - 1]];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKey);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKey);
      openCount -= 1;
      if (openCount === 0) document.body.style.overflow = '';
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus({ preventScroll: true });
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-3" role="presentation">
      <div
        className="absolute inset-0 bg-gray-900/50 backdrop-blur-[1px] animate-fade-in"
        aria-hidden="true"
        onClick={closeOnBackdrop ? onClose : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy || fallbackId}
        tabIndex={-1}
        className={`modal-panel relative w-full ${panelClassName} bg-white shadow-xl rounded-t-2xl sm:rounded-xl flex flex-col text-left outline-none animate-slide-up overflow-hidden`}
      >
        <div className="sm:hidden flex justify-center pt-2" aria-hidden="true">
          <span className="h-1 w-10 rounded-full bg-gray-300" />
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
};

export default ModalShell;
